<script setup lang="ts">
import { onMounted } from 'vue';
import BaseSelect from '@/components/ui/BaseSelect.vue';
import {
  useSchoolHolidays,
  type SupportedHolidayCountry,
} from '@/composables/holidays/useSchoolHolidays';
import { $t } from '@/composables/translations/useTranslate';
import type { DateRange } from '@/types/SearchBar';

interface Props {
  modelValue: string | null;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
  select: [range: DateRange];
}>();

const SUPPORTED_COUNTRIES: ReadonlyArray<{ slug: SupportedHolidayCountry; label: string }> = [
  { slug: 'deutschland', label: $t('ibe.holiday.country-code-de', 'DE') },
  { slug: 'oesterreich', label: $t('ibe.holiday.country-code-at', 'AT') },
];

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}

const {
  selectedCountry,
  selectedState,
  stateOptions,
  holidayOptions,
  isLoadingStates,
  isLoadingHolidays,
  error,
  selectCountry,
  selectState,
  optionToDateRange,
} = useSchoolHolidays(
  () => toDate(props.minDate),
  () => toDate(props.maxDate)
);

onMounted(() => {
  void selectCountry(selectedCountry.value);
});

function handleCountryClick(country: SupportedHolidayCountry): void {
  if (country === selectedCountry.value) return;
  emit('update:modelValue', null);
  void selectCountry(country);
}

function handleStateChange(value: string | number | null): void {
  if (value === null) return;
  emit('update:modelValue', null);
  void selectState(String(value));
}

function handleHolidayChange(value: string | number | null): void {
  if (typeof value !== 'string') {
    emit('update:modelValue', null);
    return;
  }
  const range = optionToDateRange(value);
  if (!range) return;
  emit('update:modelValue', value);
  emit('select', range);
}

function countryButtonClass(active: boolean): string {
  const base =
    'w-8 h-8 sm:w-10 sm:h-10 inline-flex items-center justify-center text-sm font-medium transition-colors duration-200';
  return active
    ? `${base} bg-[#feede9] text-text-primary`
    : `${base} bg-transparent text-text-primary hover:bg-[#f9f6ee]`;
}
</script>

<template>
  <div class="holiday-section">
    <div class="flex items-center gap-3 mb-2.5">
      <h3 class="text-xl font-semibold text-text-primary leading-[30px]">
        {{ $t('ibe.holiday.section-title', 'School holidays') }}
      </h3>
      <div class="flex gap-2" role="group" :aria-label="$t('ibe.holiday.country-label', 'Country')">
        <button
          v-for="country in SUPPORTED_COUNTRIES"
          :key="country.slug"
          type="button"
          :class="countryButtonClass(country.slug === selectedCountry)"
          :aria-pressed="country.slug === selectedCountry"
          @click="handleCountryClick(country.slug)"
        >
          {{ country.label }}
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-8">
      <BaseSelect
        :model-value="selectedState"
        :options="stateOptions"
        :placeholder="
          isLoadingStates
            ? $t('ibe.holiday.loading', 'Loading…')
            : $t('ibe.holiday.state-placeholder', 'Select state')
        "
        :loading="isLoadingStates"
        :disabled="stateOptions.length === 0"
        max-width="max-w-full"
        :aria-label="$t('ibe.holiday.state-label', 'State')"
        @update:model-value="handleStateChange"
      />

      <BaseSelect
        :model-value="props.modelValue"
        :options="holidayOptions"
        :placeholder="
          isLoadingHolidays
            ? $t('ibe.holiday.loading', 'Loading…')
            : $t('ibe.holiday.holiday-placeholder', 'Select holidays')
        "
        :loading="isLoadingHolidays"
        :disabled="holidayOptions.length === 0"
        max-width="max-w-full"
        :aria-label="$t('ibe.holiday.holiday-label', 'School holidays')"
        @update:model-value="handleHolidayChange"
      />
    </div>

    <p v-if="error" class="text-sm text-error mt-2" role="alert">
      {{ $t('ibe.holiday.error', 'Holidays could not be loaded') }}
    </p>
  </div>
</template>
